import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Linkedin, MessageCircle } from "lucide-react";
import { useI18n } from "../context/I18nContext";

// Reusable hook — navigate to homepage section from any page
function useSectionNav() {
  const navigate = useNavigate();
  const location = useLocation();
  return function goTo(sectionId) {
    if (location.pathname === "/") {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    }
  };
}

export function Footer({ settings = {} }) {
  const { t } = useI18n();
  const goTo = useSectionNav();

  const phone   = settings.phone || "";
  const email   = settings.email || "info@ygs.com.tr";
  const address = settings.address || "İkitelli OSB, Atatürk Bulvarı No: 124, Başakşehir / İstanbul";
  const instagram = settings.instagram || "https://instagram.com";
  const linkedin  = settings.linkedin || "https://linkedin.com";
  const whatsapp = settings.whatsapp || (settings.whatsapp_phone ? `https://wa.me/${settings.whatsapp_phone.replace(/\D/g, "")}` : "");

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0d1b2a] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-8">

          {/* Brand */}
          <div className="md:col-span-2 lg:col-span-5">
            <Link to="/" className="inline-flex bg-white/5 border border-white/10 rounded-xl p-4" data-testid="footer-logo">
              <img
                src="/logo.png"
                alt="YGS Yapı Güvenlik Sistemleri"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="mt-5 text-white/70 max-w-md text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={instagram} target="_blank" rel="noreferrer"
                 data-testid="footer-instagram"
                 className="w-10 h-10 rounded-md border border-white/15 flex items-center justify-center hover:bg-[#ffb703] hover:text-[#0d1b2a] hover:border-[#ffb703] transition-all">
                <Instagram size={16} />
              </a>
              <a href={linkedin} target="_blank" rel="noreferrer"
                 data-testid="footer-linkedin"
                 className="w-10 h-10 rounded-md border border-white/15 flex items-center justify-center hover:bg-[#ffb703] hover:text-[#0d1b2a] hover:border-[#ffb703] transition-all">
                <Linkedin size={16} />
              </a>
              {whatsapp && (
                <a href={whatsapp} target="_blank" rel="noreferrer"
                   data-testid="footer-whatsapp"
                   className="w-10 h-10 rounded-md border border-white/15 flex items-center justify-center hover:bg-[#ffb703] hover:text-[#0d1b2a] hover:border-[#ffb703] transition-all">
                <MessageCircle size={16} />
              </a>
              )}
            </div>
          </div>

          {/* Company — section navigation */}
          <div className="lg:col-span-5">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#ffb703] mb-4">
              {t.footer.company}
            </h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              {[
                { section: "about",      label: t.nav.about },
                { section: "references", label: t.nav.references },
                { section: "services",   label: t.nav.services },
                { section: "contact",    label: t.nav.contact },
              ].map(({ section, label }) => (
                <li key={section}>
                  <button
                    onClick={() => goTo(section)}
                    className="hover:text-[#ffb703] transition-colors bg-transparent border-none cursor-pointer text-white/75 text-sm"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2 lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#ffb703] mb-4">
              {t.nav.contact}
            </h4>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Phone size={14} className="mt-1 text-white/50 shrink-0" />
                <div>
                  <a href={`tel:${phone}`} className="hover:text-[#ffb703] transition-colors block">{phone}</a>
                  {settings.phone2 && (
                    <a href={`tel:${settings.phone2}`} className="hover:text-[#ffb703] transition-colors block mt-0.5">{settings.phone2}</a>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="mt-1 text-white/50 shrink-0" />
                <a href={`mailto:${email}`} className="hover:text-[#ffb703] transition-colors break-all">{email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-white/50 shrink-0" />
                <span>{address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/55">
          <p data-testid="footer-copyright">{t.footer.copyright}</p>
          <div className="flex items-center gap-5">
            <button onClick={() => goTo("contact")} className="hover:text-[#ffb703] transition-colors bg-transparent border-none cursor-pointer text-white/55 text-xs">
              {t.footer.kvkk}
            </button>
            <button onClick={() => goTo("contact")} className="hover:text-[#ffb703] transition-colors bg-transparent border-none cursor-pointer text-white/55 text-xs">
              {t.footer.privacy}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
